import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const INIT = { name: '', sku: '', category: '', price: '', cost: '', stock: '', unit: 'pcs', status: 'active' };
const UNITS = ['pcs', 'kg', 'g', 'litre', 'box', 'pack', 'set', 'm', 'cm'];
const LIMIT = 15;

export default function Products({ setTitle }) {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState(INIT);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);

  useEffect(() => { setTitle('Products'); }, [setTitle]);

  useEffect(() => {
    api.get('/products/categories').then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/products?search=${encodeURIComponent(search)}&category=${encodeURIComponent(category)}&page=${page}&limit=${LIMIT}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [search, category, page]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(INIT); setEditId(null); setModal(true); };
  const openEdit = (item) => { setForm({ ...item, price: item.price ?? '', cost: item.cost ?? '', stock: item.stock ?? '' }); setEditId(item.id); setModal(true); };
  const closeModal = () => setModal(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Product name is required');
    setSaving(true);
    try {
      if (editId) await api.put(`/products/${editId}`, form);
      else        await api.post('/products', form);
      closeModal(); load();
      api.get('/products/categories').then(r => setCategories(r.data || [])).catch(() => {});
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving product');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/products/${id}`); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error deleting product');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Products</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Product
        </button>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search by name, SKU…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <select className="form-control" style={{ width: 160 }} value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} products</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>SKU</th>
                  <th>Category</th>
                  <th className="text-right">Price</th>
                  <th className="text-right">Cost</th>
                  <th className="text-right">Stock</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={9} className="empty">No products found</td></tr>
                ) : items.map((item, i) => (
                  <tr key={item.id}>
                    <td className="text-muted">{(page - 1) * LIMIT + i + 1}</td>
                    <td><strong>{item.name}</strong></td>
                    <td className="font-mono text-muted">{item.sku || '—'}</td>
                    <td>{item.category || '—'}</td>
                    <td className="text-right">${parseFloat(item.price || 0).toFixed(2)}</td>
                    <td className="text-right">${parseFloat(item.cost || 0).toFixed(2)}</td>
                    <td className="text-right">
                      <span className={`stock-badge ${Number(item.stock) < 10 ? 'stock-low' : 'stock-ok'}`}>
                        {item.stock} {item.unit}
                      </span>
                    </td>
                    <td><span className={`badge badge-${item.status}`}>{item.status}</span></td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.name)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId ? 'Edit Product' : 'Add Product'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Product Name *</label>
                  <input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} placeholder="Product name" />
                </div>
                <div className="form-group">
                  <label>SKU</label>
                  <input className="form-control" value={form.sku} onChange={e => f('sku', e.target.value)} placeholder="SKU-001" />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <input className="form-control" value={form.category} onChange={e => f('category', e.target.value)} placeholder="Electronics" list="cat-list" />
                  <datalist id="cat-list">
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div className="form-group">
                  <label>Selling Price</label>
                  <input type="number" className="form-control" value={form.price} onChange={e => f('price', e.target.value)} placeholder="0.00" step="0.01" min="0" />
                </div>
                <div className="form-group">
                  <label>Cost Price</label>
                  <input type="number" className="form-control" value={form.cost} onChange={e => f('cost', e.target.value)} placeholder="0.00" step="0.01" min="0" />
                </div>
                <div className="form-group">
                  <label>Stock Qty</label>
                  <input type="number" className="form-control" value={form.stock} onChange={e => f('stock', e.target.value)} placeholder="0" min="0" />
                </div>
                <div className="form-group">
                  <label>Unit</label>
                  <select className="form-control" value={form.unit} onChange={e => f('unit', e.target.value)}>
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.status} onChange={e => f('status', e.target.value)}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Product' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
